import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { decryptData } from "../../utils/dataEncoder";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addTemplateDataFailure,
  addTemplateDataSuccess,
  deleteFileFailure,
  deleteFileSuccess,
  deleteVersionFailure,
  deleteVersionSuccess,
  getCareSummaryFailure,
  getCareSummarySuccess,
  getCurrentTemplateFailure,
  getCurrentTemplateSuccess,
  getRevisionFailure,
  getRevisionSuccess,
  getPreviewFailure,
  getPreviewSuccess,
  getTemplateDataFailure,
  getTemplateDataSuccess,
  getVersionDataFailure,
  getVersionDataSuccess,
  saveSnapshotFailure,
  saveSnapshotSuccess,
  saveTemplateDataFailure,
  saveTemplateDataSuccess,
  searchTemplatesFailure,
  searchTemplatesSuccess,
  updateTemplateDataFailure,
  updateTemplateDataSuccess,
  uploadFileFailure,
  uploadFileSuccess,
  viewRevisionFailure,
  viewRevisionSuccess,
  getGovernanceStatsFailure,
  getGovernanceStatsSuccess,
  updatePlanStatusFailure,
  updatePlanStatusSuccess,
} from "./actions";
import {
  ADD_TEMPLATE_DATA,
  DELETE_FILE,
  DELETE_VERSION,
  GET_CARE_SUMMARY,
  GET_CURRENT_TEMPLATE,
  GET_REVISION,
  GET_PREVIEW,
  GET_TEMPLATE_DATA,
  GET_VERSION_DATA,
  RECORD_OBSERVATION,
  SAVE_SNAPSHOT,
  SAVE_TEMPLATE_DATA,
  SEARCH_TEMPLATES,
  UPDATE_TEMPLATE_DATA,
  UPLOAD_FILE,
  VIEW_REVISION,
  GET_GOVERNANCE_STATS,
  UPDATE_PLAN_STATUS,
} from "./constants";

/**
 * utils/api's error interceptor decrypts the body only far enough to build the
 * message string, then discards the payload — re-derive the envelope so a
 * machine-readable `reason` is readable here. Same helper shape as
 * Stores/IncidentRecord/saga.js, for the same reason.
 */
const responseBody = (err) => {
  const raw = err?.response?.data;
  if (!raw) return null;
  if (raw.encrypted === true && typeof raw.data === "string") {
    try {
      return decryptData(raw.data);
    } catch (_) {
      return null;
    }
  }
  return raw;
};

/**
 * 🔴 THE AI HUMAN-REVIEW GATE, client side. The server refuses a save that
 * carries AI content nobody acknowledged with
 * `422 { data: { reason: 'ai_review_required' } }` — a RECOVERABLE refusal: the
 * caller re-sends the same payload with `ai_authoring.reviewed_ack` once the
 * clinician confirms. It is currently in SHADOW mode server-side (it logs and
 * lets the save through), so this path is dormant until the mode flips.
 *
 * Returns true when the caller's `onReviewRequired` claimed the refusal, so the
 * generic red error alert stays out of the way of the confirm dialog. Every
 * other failure — and every caller that passes no handler — falls through to
 * the existing behaviour untouched.
 */
const claimedByReviewGate = (e, onReviewRequired) => {
  if (typeof onReviewRequired !== "function") return false;
  if (e?.response?.status !== 422) return false;
  const body = responseBody(e);
  if (body?.data?.reason !== "ai_review_required") return false;
  onReviewRequired({
    reason: "ai_review_required",
    message: typeof body?.message === "string" ? body.message : "",
  });
  return true;
};

/**
 * 🔴 THE WOUND ↔ CARE-PLAN BINDING, client side. One plan per wound: the server
 * refuses a create that names a wound already bound to another plan with
 * `409 { data: { reason: 'wound_already_planned', existing_care_plan_id } }`.
 *
 * A create may now name SEVERAL wounds, so the refusal may be about several: the
 * body also carries `conflicts: [{ subject_uuid, label, existing_care_plan_id }]`
 * naming each one. `existing_care_plan_id` stays at the top level as the FIRST
 * conflict, unchanged, so nothing that predates the array has to move. The array
 * is passed straight through — the dialog is the only thing that knows how to
 * say "and".
 *
 * NOT retryable — re-sending the identical payload would be refused identically,
 * and silently re-pointing the wound would ORPHAN the plan it already has (its
 * treatment history would survive with nothing linking to it, and the register
 * would report the wrong regimen for the wound in front of the nurse). So the
 * caller is handed the existing plan's id and offers to open it; nothing is
 * written either way.
 *
 * Returns true when the caller's `onWoundConflict` claimed the refusal, so the
 * generic red error alert stays out of the way of its dialog. Same envelope
 * decrypt + same shape as claimedByReviewGate above.
 */
const claimedByWoundConflict = (e, onWoundConflict) => {
  if (typeof onWoundConflict !== "function") return false;
  if (e?.response?.status !== 409) return false;
  const body = responseBody(e);
  if (body?.data?.reason !== "wound_already_planned") return false;
  onWoundConflict({
    reason: "wound_already_planned",
    existing_care_plan_id: body?.data?.existing_care_plan_id ?? null,
    conflicts: Array.isArray(body?.data?.conflicts) ? body.data.conflicts : [],
    message: typeof body?.message === "string" ? body.message : "",
  });
  return true;
};

export function* getPreviewSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${payload}/response-summary`,
    );
    yield put(getPreviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getPreviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching network!"));
  }
}

export function* getTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId } = payload;
  const url = `/api/resident-template-response/${residentId}`;
  try {
    const { data } = yield call(api(token).get, url);
    yield put(getTemplateDataSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getTemplateDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching template data!"));
  }
}

export function* saveTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
  onReviewRequired,
  onWoundConflict,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/add`,
      payload,
    );
    yield put(saveTemplateDataSuccess(data));
    yield put(successAlert("Data saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveTemplateDataFailure());
    if (claimedByReviewGate(e, onReviewRequired)) return;
    if (claimedByWoundConflict(e, onWoundConflict)) return;
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving template data!"));
  }
}

export function* uploadFileSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/upload-document`,
      payload,
    );
    yield put(uploadFileSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(uploadFileFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while uploading file!"));
  }
}

export function* deleteFileSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-template-response/delete-document`,
      payload,
    );
    yield put(deleteFileSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteFileFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting file!"));
  }
}

export function* searchTemplatesSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, search_term, include_used } = payload;
  try {
    let url = `/api/assessment-template/pending-list/${residentId}`;
    const query = {};
    if (search_term && search_term.trim())
      query.search_term = search_term.trim();
    if (include_used) query.include_used = 1;
    const queryParams = QueryString.stringify(query);
    if (queryParams) url = `${url}?${queryParams}`;
    const { data } = yield call(api(token).get, url);
    yield put(searchTemplatesSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(searchTemplatesFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while searching templates!"));
  }
}

export function* getCareSummarySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${payload}/categorized-assessments`,
    );
    yield put(getCareSummarySuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getCareSummaryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching care summary!"));
  }
}

export function* getVersionDataSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-template-version?${query}`,
    );
    yield put(getVersionDataSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getVersionDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching care summary!"));
  }
}

export function* updateTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
  onReviewRequired,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/update/${payload.id}`,
      payload.data,
    );
    yield put(updateTemplateDataSuccess(data));
    yield put(successAlert("Data updated successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(updateTemplateDataFailure());
    if (claimedByReviewGate(e, onReviewRequired)) return;
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating template data!"));
  }
}

export function* saveSnapshotSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/save-resident-tool-snapshort`,
      payload,
    );
    yield put(saveSnapshotSuccess(data));
    yield put(successAlert("Snapshot saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveSnapshotFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving snapshot!"));
  }
}

export function* deleteVersionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-template-response/${payload.id}`,
    );
    yield put(deleteVersionSuccess(data));
    yield put(successAlert("Version deleted successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteVersionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting version!"));
  }
}

export function* getCurrentTemplateSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const url = `/api/resident-template-response-details/${payload.carePlanId}`;
  try {
    const { data } = yield call(api(token).get, url);
    yield put(getCurrentTemplateSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getCurrentTemplateFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching current template!"));
  }
}

/** Flatten a catalog-shaped payload into multipart FormData (when photos ride along). */
function buildObservationFormData(payload) {
  const fd = new FormData();
  const { data = {}, body_location = [], upload_files = [], ...rest } = payload;
  Object.entries(rest).forEach(([k, v]) => {
    if (v !== null && v !== undefined) fd.append(k, v);
  });
  Object.entries(data || {}).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((x, i) => fd.append(`data[${k}][${i}]`, x));
    else if (v !== null && v !== undefined) fd.append(`data[${k}]`, v);
  });
  (body_location || []).forEach((r, i) => fd.append(`body_location[${i}]`, r));
  (upload_files || []).forEach((f, i) => fd.append(`upload_files[${i}]`, f));
  return fd;
}

/**
 * v2 catalog-driven recording. Posts a catalog-shaped payload to the new
 * /api/observations endpoint (legacy-first under the hood — charts + body-map
 * tab keep working). JSON normally; multipart FormData when photos are attached.
 * The component owns the submitting state, so this saga just resolves
 * onSuccess / onError.
 */
export function* recordObservationSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const hasFiles =
      Array.isArray(payload?.upload_files) && payload.upload_files.length > 0;
    let body = payload;
    if (hasFiles) {
      body = buildObservationFormData(payload);
    } else {
      const { upload_files, ...rest } = payload || {};
      body = rest;
    }
    const { data } = yield call(api(token).post, `/api/observations`, body);
    yield put(successAlert("Observation recorded"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    const msg =
      typeof e === "string"
        ? e
        : e?.error || e?.message || "Error while recording observation!";
    yield put(errorAlert(msg));
    typeof onError === "function" && onError(e);
  }
}

export function* addTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
  onReviewRequired,
  onWoundConflict,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-care-plan/save`,
      payload,
    );
    yield put(addTemplateDataSuccess(data));
    yield put(successAlert("Care plan saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(addTemplateDataFailure());
    if (claimedByReviewGate(e, onReviewRequired)) return;
    if (claimedByWoundConflict(e, onWoundConflict)) return;
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving care plan!"));
  }
}

export function* getRevisionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-version-history/${payload}`,
    );
    yield put(getRevisionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getRevisionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching revision!"));
  }
}

export function* viewRevisionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-version-revision?${query}`,
    );
    yield put(viewRevisionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(viewRevisionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while viewing revision!"));
  }
}

export function* getGovernanceStatsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  // payload is either a residentId string, or { residentId, domainKey } for a
  // domain-scoped (e.g. safeguarding) view.
  const residentId =
    payload && typeof payload === "object" ? payload.residentId : payload;
  const domainKey =
    payload && typeof payload === "object" ? payload.domainKey : null;
  try {
    const url = domainKey
      ? `/api/resident/${residentId}/governance-item/status-stats?domain_key=${encodeURIComponent(domainKey)}`
      : `/api/resident/${residentId}/governance-item/status-stats`;
    const { data } = yield call(api(token).get, url);
    // Only the canonical all-domain stats populate the shared slot that drives
    // the Alerts rail count; domain-scoped stats are delivered via callback so
    // they don't clobber it.
    if (!domainKey) {
      yield put(getGovernanceStatsSuccess(data));
    }
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getGovernanceStatsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching governance stats!"));
  }
}

export function* updatePlanStatusSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { carePlanId } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-care-plan/${carePlanId}/status`,
      payload,
    );
    yield put(updatePlanStatusSuccess(data));
    yield put(successAlert("Status updated successfully!"));
    typeof onSuccess === "function" && onSuccess();
  } catch (e) {
    yield put(updatePlanStatusFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating status!"));
  }
}

export function* getPreviewFlow() {
  yield takeEvery(GET_PREVIEW, getPreviewSaga);
}
export function* getTemplateDataFlow() {
  yield takeEvery(GET_TEMPLATE_DATA, getTemplateDataSaga);
}
export function* saveTemplateDataFlow() {
  yield takeEvery(SAVE_TEMPLATE_DATA, saveTemplateDataSaga);
}
export function* uploadFileFlow() {
  yield takeEvery(UPLOAD_FILE, uploadFileSaga);
}
export function* deleteFileFlow() {
  yield takeEvery(DELETE_FILE, deleteFileSaga);
}
export function* searchTemplatesFlow() {
  yield takeEvery(SEARCH_TEMPLATES, searchTemplatesSaga);
}
export function* getCareSummaryFlow() {
  yield takeEvery(GET_CARE_SUMMARY, getCareSummarySaga);
}
export function* getVersionDataFlow() {
  yield takeEvery(GET_VERSION_DATA, getVersionDataSaga);
}
export function* updateTemplateDataFlow() {
  yield takeEvery(UPDATE_TEMPLATE_DATA, updateTemplateDataSaga);
}
export function* saveSnapshotFlow() {
  yield takeEvery(SAVE_SNAPSHOT, saveSnapshotSaga);
}
export function* deleteVersionFlow() {
  yield takeEvery(DELETE_VERSION, deleteVersionSaga);
}
export function* getCurrentTemplateFlow() {
  yield takeEvery(GET_CURRENT_TEMPLATE, getCurrentTemplateSaga);
}
export function* saveObservationFlow() {
  yield takeEvery(RECORD_OBSERVATION, recordObservationSaga);
}
export function* addTemplateDataFlow() {
  yield takeEvery(ADD_TEMPLATE_DATA, addTemplateDataSaga);
}
export function* getRevisionFlow() {
  yield takeEvery(GET_REVISION, getRevisionSaga);
}
export function* viewRevisionFlow() {
  yield takeEvery(VIEW_REVISION, viewRevisionSaga);
}
export function* getGovernanceStatsFlow() {
  yield takeEvery(GET_GOVERNANCE_STATS, getGovernanceStatsSaga);
}
export function* updatePlanStatusFlow() {
  yield takeEvery(UPDATE_PLAN_STATUS, updatePlanStatusSaga);
}
export default function* ResidentAssessmentSaga() {
  yield all([
    getPreviewFlow(),
    getTemplateDataFlow(),
    saveTemplateDataFlow(),
    uploadFileFlow(),
    deleteFileFlow(),
    searchTemplatesFlow(),
    getCareSummaryFlow(),
    getVersionDataFlow(),
    updateTemplateDataFlow(),
    saveSnapshotFlow(),
    deleteVersionFlow(),
    getCurrentTemplateFlow(),
    saveObservationFlow(),
    addTemplateDataFlow(),
    getRevisionFlow(),
    viewRevisionFlow(),
    getGovernanceStatsFlow(),
    updatePlanStatusFlow(),
  ]);
}
